/**
 * 消息中心 API
 * 统一返回经过全局响应拦截器转换后的 camelCase 数据。
 * 对应 foundation-service (7092)
 *
 * 两组端点:
 *   用户端: /messages, /announcements
 *   管理端: /notifications/announcements
 *
 * 后端代码: foundation-service/api/internal/handler/notification_handler.go
 * 更新日期: 2026-02-25
 */

import { unifiedApi } from '@/utils/unifiedApi';
import { get } from '@/utils/request';
import type {
  MessageInfo,
  MessageQueryParams,
  MessageMarkReadReq,
  MessageDeleteReq,
  MessageUnreadCountResp,
  MessageBatchOperationResp,
  MessageDetailResp,
  AnnouncementInfo,
  AnnouncementQueryParams,
  AnnouncementDetailResp,
  CreateAnnouncementReq,
  UpdateAnnouncementReq,
  AnnouncementAudiencePreviewResp,
  AnnouncementStatisticsResp,
  ChannelCustomerTenantsResp,
} from './types';

const service = () => unifiedApi.service('user');

interface ServiceResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

const unwrap = async <T>(request: Promise<ServiceResponse<T>>): Promise<T> => {
  const response = await request;
  if (!response.success) {
    throw new Error(response.message || '请求失败');
  }
  return response.data;
};

// ==================== 用户端 — 消息 ====================

/** 获取消息列表 */
export const getMessageList = (params?: MessageQueryParams) => unwrap(
  service().get<{ list: MessageInfo[]; total: number; page: number; pageSize: number }>(
    '/messages',
    { params }
  )
);

/** 获取消息详情（自动标记已读） */
export const getMessageDetail = (id: number) => unwrap(
  service().get<MessageDetailResp>(`/messages/${id}`)
);

/** 标记消息已读 */
export const markMessagesRead = (data: MessageMarkReadReq) => unwrap(
  service().post<MessageBatchOperationResp>('/messages/mark-read', data)
);

/** 删除消息 */
export const deleteMessages = (data: MessageDeleteReq) => unwrap(
  service().post<MessageBatchOperationResp>('/messages/delete', data)
);

/** 获取未读消息数 */
export const getUnreadCount = () => unwrap(
  service().get<MessageUnreadCountResp>('/messages/unread-count')
);

export const getMessagePopups = () => unwrap(
  service().get<{ list: MessageInfo[] }>('/messages/popups')
);

export const recordMessageImpression = (id: number) => unwrap(service().post(`/messages/${id}/impression`));
export const dismissMessage = (id: number) => unwrap(service().post(`/messages/${id}/dismiss`));

// ==================== 用户端 — 公告 ====================

/** 获取公告列表（仅已发布且未过期） */
export const getAnnouncementList = (params?: AnnouncementQueryParams) => unwrap(
  service().get<{ list: AnnouncementInfo[]; total: number; page: number; pageSize: number }>(
    '/announcements',
    { params }
  )
);

/** 获取公告详情（自动增加浏览量） */
export const getAnnouncementDetail = (id: number) => unwrap(
  service().get<AnnouncementDetailResp>(`/announcements/${id}`)
);

/** 公告全部标记已读（不影响消息） */
export const markAllAnnouncementsRead = () => unwrap(
  service().post<MessageBatchOperationResp>('/announcements/mark-all-read')
);

// ==================== 管理端 — 公告 CRUD ====================

/** 获取公告管理列表（含草稿、已下架） */
export const getAdminAnnouncementList = (params?: AnnouncementQueryParams) => unwrap(
  service().get<{ list: AnnouncementInfo[]; total: number; page: number; pageSize: number }>(
    '/notifications/announcements',
    { params }
  )
);

/** 获取公告详情（管理端） */
export const getAdminAnnouncementDetail = (id: number) => unwrap(
  service().get<AnnouncementInfo>(`/notifications/announcements/${id}`)
);

/** 创建公告 */
export const createAnnouncement = (data: CreateAnnouncementReq) => unwrap(
  service().post<AnnouncementInfo>('/notifications/announcements', data)
);

/** 更新公告 */
export const updateAnnouncement = (id: number, data: UpdateAnnouncementReq) => unwrap(
  service().put(`/notifications/announcements/${id}`, data)
);

/** 删除公告 */
export const deleteAnnouncement = (id: number) => unwrap(
  service().delete(`/notifications/announcements/${id}`)
);

export const createAnnouncementVersion = (id: number) => unwrap(
  service().post<AnnouncementInfo>(`/notifications/announcements/${id}/version`)
);

/** 发布公告 */
export const publishAnnouncement = (id: number) => unwrap(
  service().post<{ sentCount: number; deliveryStatus: AnnouncementInfo['deliveryStatus'] }>(`/notifications/announcements/${id}/publish`)
);

/** 下架公告 */
export const unpublishAnnouncement = (id: number) => unwrap(
  service().post(`/notifications/announcements/${id}/unpublish`)
);

export const previewAnnouncementAudience = (data: Pick<CreateAnnouncementReq, 'audienceMode' | 'recipientMode' | 'includeSelf' | 'includeDescendants' | 'targetTenantIds'>) => unwrap(
  service().post<AnnouncementAudiencePreviewResp>('/notifications/announcements/preview-audience', data)
);

export const getAnnouncementStatistics = (id: number) => unwrap(
  service().get<AnnouncementStatisticsResp>(`/notifications/announcements/${id}/statistics`)
);

export const retryAnnouncementDelivery = (id: number) => unwrap(
  service().post(`/notifications/announcements/${id}/retry-delivery`)
);

export const getAnnouncementPopups = () => unwrap(
  service().get<{ list: AnnouncementInfo[] }>('/announcements/popups')
);

/** 当前用户常驻横幅公告（每日首次弹窗策略） */
export const getAnnouncementBanners = () => unwrap(
  service().get<{ list: AnnouncementInfo[] }>('/announcements/banners')
);

/** 按渠道查本租户命中的客户租户（ERP），公告「按渠道」圈选用 */
export const getChannelCustomerTenants = (channelIds: number[]) =>
  get<ChannelCustomerTenantsResp>('/api/v1/customers/channel-tenants', { channelIds: channelIds.join(',') });

export const recordAnnouncementImpression = (id: number) => unwrap(service().post(`/announcements/${id}/impression`));
export const dismissAnnouncement = (id: number) => unwrap(service().post(`/announcements/${id}/dismiss`));
export const acknowledgeAnnouncement = (id: number) => unwrap(service().post(`/announcements/${id}/acknowledge`));

// ==================== 导出 API 对象 ====================

export const messageCenterApi = {
  // 用户端 — 消息
  getMessageList,
  getMessageDetail,
  markMessagesRead,
  deleteMessages,
  getUnreadCount,
  getMessagePopups,
  recordMessageImpression,
  dismissMessage,
  // 用户端 — 公告
  getAnnouncementList,
  getAnnouncementDetail,
  markAllAnnouncementsRead,
  // 管理端 — 公告 CRUD
  getAdminAnnouncementList,
  getAdminAnnouncementDetail,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  createAnnouncementVersion,
  publishAnnouncement,
  unpublishAnnouncement,
  previewAnnouncementAudience,
  getAnnouncementStatistics,
  retryAnnouncementDelivery,
  getAnnouncementPopups,
  getAnnouncementBanners,
  getChannelCustomerTenants,
  recordAnnouncementImpression,
  dismissAnnouncement,
  acknowledgeAnnouncement,
};

export default messageCenterApi;
